<template>
  <div>
    <Nav />
    <ModuleForm
      v-if="showModuleForm"
      v-on:closeModule="showModuleForm=false"
      v-on:newModule="refreshModules"
    />
    <div class="module-list container">
      <div class="columns">
        <div class="column is-one-quarter">
          <div class="content">
            <p>
              <a
                class="button is-primary is-fullwidth is-medium"
                v-on:click="showModuleForm=true"
              >Add your own module</a>
            </p>
          </div>
          <article class="message" v-if="types.length>0">
            <div class="message-header">
              <p>Activity Type</p>
              <a class="reset" v-if="this.selectedType!=null" v-on:click="resetTypeFilter()">
                <span class="icon is-small">
                  <i class="fas fa-undo"></i>
                </span>
              </a>
            </div>
            <div class="message-body">
              <div class="field">
                <div class="control" v-for="type in types" :key="'type-'+type.id">
                  <label class="radio">
                    <input
                      type="radio"
                      :value="type.id"
                      v-model="selectedType"
                      v-on:change="refreshFilter()"
                    />
                    {{type.name}}
                    <span class="tag is-warning">{{type.count}}</span>
                  </label>
                </div>
              </div>
            </div>
          </article>

          <article class="message" v-if="grades.length>0">
            <div class="message-header">
              <p>Grade</p>
              <a class="reset" v-if="this.selectedGrade!=null" v-on:click="resetGradeFilter()">
                <span class="icon is-small">
                  <i class="fas fa-undo"></i>
                </span>
              </a>
            </div>
            <div class="message-body">
              <div class="field">
                <div class="control" v-for="grade in grades" :key="'grade-'+grade.id">
                  <label class="radio">
                    <input
                      type="radio"
                      :value="grade.id"
                      v-model="selectedGrade"
                      v-on:change="refreshFilter()"
                    />
                    {{grade.name}}
                    <span class="tag is-info">{{grade.count}}</span>
                  </label>
                </div>
              </div>
            </div>
          </article>

          <article class="message" v-if="subjects.length>0">
            <div class="message-header">
              <p>Subject</p>
              <a class="reset" v-if="this.selectedSubject!=null" v-on:click="resetSubjectFilter()">
                <span class="icon is-small">
                  <i class="fas fa-undo"></i>
                </span>
              </a>
            </div>
            <div class="message-body">
              <div class="field">
                <div class="control" v-for="subject in subjects" :key="'subject-'+subject.id">
                  <label class="radio">
                    <input
                      type="radio"
                      :value="subject.id"
                      v-model="selectedSubject"
                      v-on:change="refreshFilter()"
                    />
                    {{subject.name}}
                    <span
                      class="tag is-success"
                    >{{subject.count}}</span>
                  </label>
                </div>
              </div>
            </div>
          </article>

          <article class="message" v-if="units.length>0">
            <div class="message-header">
              <p>Unit</p>
              <a class="reset" v-if="this.selectedUnit!=null" v-on:click="resetUnitFilter()">
                <span class="icon is-small">
                  <i class="fas fa-undo"></i>
                </span>
              </a>
            </div>
            <div class="message-body">
              <div class="field">
                <div class="control" v-for="unit in units" :key="'unit-'+unit.id">
                  <label class="radio">
                    <input
                      type="radio"
                      :value="unit.id||-1"
                      v-model="selectedUnit"
                      v-on:change="refreshFilter()"
                    />
                    {{unit.name||"N/A"}}
                    <span
                      class="tag is-danger"
                    >{{unit.count}}</span>
                  </label>
                </div>
              </div>
            </div>
          </article>
        </div>

        <div class="column">
          <router-link
            :to="{name: 'module', params: {id: module.id, listProps, fromList:true}}"
            v-for="module in modules"
            :key="module.id"
          >
            <div class="box module">
              <h3 class="title">{{module.title}}</h3>
              <div class="tags">
                <span class="tag is-medium is-warning">{{module.type}}</span>
                <span class="tag is-medium is-info" v-if="!module.isCustom">Grade {{module.grade}}</span>
                <span class="tag is-medium is-success" v-if="!module.isCustom">{{module.subject}}</span>
                <span
                  class="tag is-medium is-danger"
                  v-if="!module.isCustom"
                >Units: {{module.units||"N/A"}}</span>
              </div>
            </div>
          </router-link>

          <nav
            class="pagination is-centered"
            role="navigation"
            aria-label="pagination"
            v-if="pageCount>1"
          >
            <a class="pagination-previous" v-if="page>1" v-on:click="updatePage(page-1)">Previous</a>
            <a
              class="pagination-next"
              v-if="page<pageCount"
              v-on:click="updatePage(page+1)"
            >Next page</a>
            <ul class="pagination-list">
              <li>
                <a
                  class="pagination-link"
                  aria-label="Goto page 1"
                  v-if="page>2"
                  v-on:click="updatePage(1)"
                >1</a>
              </li>
              <li>
                <span class="pagination-ellipsis" v-if="page>2">&hellip;</span>
              </li>
              <li>
                <a
                  class="pagination-link"
                  v-if="page>pageCount-1"
                  v-on:click="updatePage(pageCount-2)"
                >{{page-2}}</a>
              </li>
              <li>
                <a class="pagination-link" v-if="page>1" v-on:click="updatePage(page-1)">{{page-1}}</a>
              </li>
              <li>
                <a class="pagination-link is-current" aria-current="page">{{page}}</a>
              </li>
              <li>
                <a
                  class="pagination-link"
                  v-on:click="updatePage(page+1)"
                  v-if="page<pageCount"
                >{{page+1}}</a>
              </li>
              <li>
                <a class="pagination-link" v-if="page<2" v-on:click="updatePage(page+2)">{{page+2}}</a>
              </li>
              <li>
                <span class="pagination-ellipsis" v-if="page<pageCount-1">&hellip;</span>
              </li>
              <li>
                <a
                  class="pagination-link"
                  v-if="page<pageCount-1"
                  v-on:click="updatePage(pageCount)"
                >{{pageCount}}</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
    <Footer />
  </div>
</template>

<script>
import axios from "axios";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import ModuleForm from "../components/ModuleForm";

export default {
  name: "ModuleList",
  props: ["parameters"],
  data() {
    if (this.parameters) {
      return { ...this.parameters };
    } else {
      return {
        modules: [],
        types: [],
        subjects: [],
        selectedType: null,
        selectedGrade: null,
        selectedSubject: null,
        selectedUnit: null,
        grades: [],
        units: [],
        count: 0,
        page: 1,
        limit: 10,
        showModuleForm: false
      };
    }
  },
  computed: {
    pageCount: function() {
      return Math.ceil(this.count / this.limit);
    },
    offset: function() {
      return (this.page - 1) * this.limit;
    },
    listProps: function() {
      return {
        modules: this.modules,
        types: this.types,
        subjects: this.subjects,
        selectedType: this.selectedType,
        selectedGrade: this.selectedGrade,
        selectedSubject: this.selectedSubject,
        selectedUnit: this.selectedUnit,
        grades: this.grades,
        units: this.units,
        count: this.count,
        page: this.page,
        limit: this.limit,
        showModuleForm: this.showModuleForm
      };
    }
  },

  mounted() {
    if (!this.parameters) {
      this.fetchData();
      this.logAction("open_module_list");
    }
  },

  methods: {
    fetchData: function() {
      const body = {
        limit: this.limit,
        offset: this.offset,
        type: this.selectedType,
        grade: this.selectedGrade,
        subject: this.selectedSubject,
        unit: this.selectedUnit
      };
      const config = this.$store.getters.config;
      axios
        .post(`${process.env.VUE_APP_API_URL}modules`, body, config)
        .then(response => {
          this.modules = response.data.modules;
          this.count = response.data.count;
          this.types = response.data.types;
          this.grades = response.data.grades;
          this.subjects = response.data.subjects;
          this.units = response.data.units;
          this.logAction("fetch_modules", body);
        });
    },
    updatePage: function(newPage) {
      if (newPage >= 1 && newPage <= this.pageCount) {
        this.page = newPage;
        this.fetchData();
        window.scrollTo(0, 0);
        this.logAction("change_page", { page: newPage });
      }
    },
    refreshFilter: function() {
      this.page = 1;
      this.fetchData();
    },
    resetTypeFilter() {
      this.selectedType = null;
      this.fetchData();
      this.logAction("reset_filter", { filter: "type" });
    },
    resetUnitFilter() {
      this.selectedUnit = null;
      this.fetchData();
      this.logAction("reset_filter", { filter: "unit" });
    },
    resetSubjectFilter() {
      this.selectedSubject = null;
      this.fetchData();
      this.logAction("reset_filter", { filter: "subject" });
    },
    resetGradeFilter() {
      this.selectedGrade = null;
      this.fetchData();
      this.logAction("reset_filter", { filter: "grade" });
    },
    refreshModules() {
      this.fetchData();
    },
    logAction: async function(action, note) {
      const body = {
        action,
        note: JSON.stringify(note)
      };
      const config = this.$store.getters.config;
      await axios.post(`${process.env.VUE_APP_API_URL}log`, body, config);
    }
  },
  components: {
    Nav,
    Footer,
    ModuleForm
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.module-list {
  margin-top: 3rem;
}
.module {
  margin-bottom: 2rem;
}
.fa-calendar:before {
  font-family: BlinkMacSystemFont, -apple-system, "Segoe UI", "Roboto", "Oxygen",
    "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue",
    "Helvetica", "Arial", sans-serif;
}

.reset {
  text-decoration: none !important;
}
</style>
